import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useIdentity } from '@bonusly/who-am-i';

import { ApprovalRequestParams, IncentiveDTO, IncentivesDTO } from '../lib/entities';
import { APIResponse, ApprovalRequestResponse, IncentiveDetailsResponse, IncentivesResponse } from '../lib/types';

enum QueryKey {
	Incentives = 'incentives',
	Incentive = 'incentive-data',
	UserProfile = 'recognition-user',
}

export const useIncentives = () => {
	const http = useHttpClient();

	return useQuery(
		[QueryKey.Incentives],
		() =>
			http.get<APIResponse<IncentivesResponse>>('/awards', {
				params: { view: 'normal', type: 'claimable' },
			}),
		{
			retry: false,
			refetchOnWindowFocus: false,
			select: (data) => {
				if (!data || !data.data || !data.data.success) {
					throw new Error('Failed to fetch incentives');
				}
				return new IncentivesDTO(data.data.result);
			},
		}
	);
};

export const useIncentive = (id?: string) => {
	const http = useHttpClient();

	return useQuery(
		[QueryKey.Incentive, id],
		() =>
			http.get<APIResponse<IncentiveDetailsResponse>>(`/awards/${id}`, {
				params: { view: 'claim_modal' },
			}),
		{
			enabled: !!id,
			retry: false,
			refetchOnWindowFocus: false,
			select: (data) => {
				if (!data || !data.data || !data.data.success || !data.data.result) {
					throw new Error(`Failed to fetch incentive with id ${id}`);
				}
				return new IncentiveDTO(data.data.result);
			},
		}
	);
};

export const useCreateApprovalRequest = (options = {}) => {
	const http = useHttpClient();
	return useMutation(
		(params: ApprovalRequestParams) => {
			const data = new FormData();
			data.append('award_id', params.id);
			data.append('giver_id', params.giverId || '');
			if (params.attachment) {
				data.append('claim_attachment', params.attachment);
			}
			if (params.reason) {
				data.append('claim_reason', params.reason);
			}

			return http.post<ApprovalRequestResponse>('/approval_requests', data);
		},
		{
			retry: false,
			onSuccess: (data: any) => {
				if (!data || !data.data || !data.data.success) {
					throw new Error('Failed to create approval request');
				}
				return data;
			},
			...options,
		}
	);
};

export const useInvalidateQueries = () => {
	const queryClient = useQueryClient();
	const { refetchProfile } = useIdentity();
	return {
		invalidate: (id?: string) => {
			queryClient.invalidateQueries([QueryKey.Incentives]);
			queryClient.invalidateQueries([QueryKey.Incentive, id]);
			// NOTE: invalidating query and refetching profile to keep both user data calls up to date
			// bonus-feed MFE uses 'recognition-user'
			queryClient.invalidateQueries([QueryKey.UserProfile]);
			refetchProfile();
		},
	};
};
